import load from 'little-loader';
import { loadCSS } from 'fg-loadcss';
import CONFIG from 'config';
import ConfigurationManager from './utils/ConfigurationManager';
import EventManager from './utils/EventManager';
import { checkOnElementsPresence, overridePageLinks, logger } from './utils/Helpers';
import { AsyncPriorityQueue } from './utils/AsyncPriorityQueue';
import mainConfig from './configs/main.config';

export default class Component {
    /**
     * Configuation manager for the component
     * @param {RcsTagManagerConfig} config
     */
    constructor(config) {
        this.queue = new AsyncPriorityQueue();
        this.confMgr = new ConfigurationManager(mainConfig[config.profileId]);
        this.loadedScripts = /** @type {Record<string, 'pending' | 'loaded' | 'error'>} */ ({});
        this.isSupportModules = 'noModule' in document.createElement('script');

        try {
            /** @public -- utility for all page scripts */
            this.eventManager = new EventManager(config.listeners);
        } catch (error) {
            logger.error(`EventManager -- `, error);
        }

        try {
            window.RCSJsonLD = JSON.parse(document.querySelector('script[type="application/ld+json"]').textContent);
        } catch (e) {}

        try {
            if (CONFIG.linkUrlParamListener && CONFIG.linkUrlParamListener.enabled) {
                logger.log('overridePageLinks for', CONFIG.linkUrlParamListener.params);
                overridePageLinks(CONFIG.linkUrlParamListener.params);
            }
        } catch (e) {}
    }

    /**
     * Load features asynchronously based on configuration
     * @returns {void}
     */
    load() {
        const initialComponents = this.confMgr.getComponents();
        const components =
            'serviceWorker' in navigator && !navigator.onLine
                ? this.getOfflineComponents(initialComponents)
                : this.getOnlineComponents(initialComponents);

        logger.log(`Number of components: ${components.length}`);

        this.loadFeatures(components);
        this.queue.start();
    }

    /**
     * @param {Array<FeatureConfig>} components
     * @returns {Array<FeatureConfig>}
     */
    getOfflineComponents(components = []) {
        return components.filter((component) => component.enableOffline || component.onlyOffline);
    }

    /**
     * @param {Array<FeatureConfig>} components
     * @returns {Array<FeatureConfig>}
     */
    getOnlineComponents(components = []) {
        return components.filter((component) => component.enableOffline || !component.onlyOffline);
    }

    /**
     * Load specified feature list in parallel
     * @param {Array<FeatureConfig>} featuresConf
     * @returns {void}
     */
    loadFeatures(featuresConf) {
        if (!featuresConf.length) return;

        const confGroups = featuresConf.reduce(
            (acc, featureConf) => {
                if (featureConf.onElementsPresence) {
                    acc.onPresenceConfs.push(featureConf);
                } else if (featureConf.onViewport) {
                    acc.onViewportConfs.push(featureConf);
                } else if (featureConf.events) {
                    acc.onEventsConfs.push(featureConf);
                } else {
                    acc.defaultConfs.push(featureConf);
                }

                return acc;
            },
            { onPresenceConfs: [], onViewportConfs: [], onEventsConfs: [], defaultConfs: [] }
        );

        this.loadDefaultsFeatures(confGroups.defaultConfs);
        this.loadDeferredFeatures(confGroups.onPresenceConfs, confGroups.onViewportConfs, confGroups.onEventsConfs);
    }

    /**
     * @param {Array<FeatureConfig>} featuresConf
     * @returns {void}
     */
    loadDefaultsFeatures(featuresConf) {
        if (!featuresConf.length) return;

        this.enqueueFeatures(featuresConf);
    }

    /**
     * @param {Array<FeatureConfig>} onPresenceConfs
     * @param {Array<FeatureConfig>} onViewportConfs
     * @param {Array<FeatureConfig>} onEventsConfs
     * @returns {void}
     */
    loadDeferredFeatures(onPresenceConfs, onViewportConfs, onEventsConfs) {
        const [featureByEvents, divider] = this.groupOnEventFeatures(onEventsConfs);

        window.addEventListener('load', () => {
            const toEnqueue = this.getOnPrecenceFeatures(onPresenceConfs);

            const onWindowLoad = `load${divider}window`;

            if (featureByEvents[onWindowLoad]) {
                toEnqueue.push(...featureByEvents[onWindowLoad]);
            }

            if (toEnqueue.length) {
                this.enqueueFeatures(toEnqueue);
            }
        });

        document.addEventListener('DOMContentLoaded', () => {
            this.initObserversForOnViewportFeatures(onViewportConfs);
            this.initFeatureEventListeners(featureByEvents, divider);

            const onContentLoad = `DOMContentLoaded${divider}document`;

            if (featureByEvents[onContentLoad]) {
                this.enqueueFeatures(featureByEvents[onContentLoad]);
            }
        });
    }

    /**
     * @param {Array<FeatureConfig>} featuresConf
     * @returns {Array<FeatureConfig>}
     */
    getOnPrecenceFeatures(featuresConf) {
        return featuresConf.filter((featureConf) => {
            const { targets } = featureConf.onElementsPresence;

            return targets && checkOnElementsPresence(targets);
        });
    }

    /**
     * @param {Array<FeatureConfig>} featuresConf
     * @returns {void}
     */
    initObserversForOnViewportFeatures(featuresConf) {
        if (!featuresConf.length) return;

        const divider = ';;';

        /** @type {Record<string, Array<FeatureConfig>>} */
        const featuresConfByViewportTarget = featuresConf.reduce((acc, featureConf) => {
            const { target, offset } = featureConf.onViewport;

            const targetArray = Array.isArray(target) ? target : [target];

            targetArray.forEach((t) => {
                if (typeof t !== 'string') {
                    logger.warn(`Unexpected onViewport configuration ::`, featureConf);

                    return;
                }

                const key = `${t}${divider}${offset || 100}`;

                acc[key] = acc[key] || [];
                acc[key].push(featureConf);
            });

            return acc;
        }, {});

        Object.entries(featuresConfByViewportTarget).forEach(([key, featureConfs]) => {
            const [target, offset] = key.split(divider);

            this.initObserverForOnViewportGroup(target, offset, featureConfs);
        });
    }

    /**
     * @param {string} target
     * @param {string} offset
     * @param {Array<FeatureConfig>} featuresConf
     * @returns {void}
     */
    initObserverForOnViewportGroup(target, offset, featuresConf) {
        const targets = document.querySelectorAll(target);

        if (!targets.length) return;

        let isLoaded = false;
        const observe = new IntersectionObserver(
            (entries) => {
                const isIntersecting = entries.some((entry) => entry.isIntersecting);

                if (isIntersecting) {
                    if (isLoaded) {
                        observe.disconnect(); // This should not be necessary. Left here for safety.
                    } else {
                        isLoaded = true;
                        this.enqueueFeatures(featuresConf);
                        observe.disconnect();
                    }
                }
            },
            { rootMargin: `${offset}px` }
        );

        targets.forEach((el) => {
            observe.observe(el);
        });
    }

    /**
     * @param {Array<FeatureConfig>} featureConfs
     * @returns {[Record<string, Array<FeatureConfig>>, string]}
     */
    groupOnEventFeatures(featureConfs) {
        const divider = ';;';
        const featureByEvents = featureConfs.reduce((acc, featureConf) => {
            featureConf.events.forEach(({ eventName, target }) => {
                if (!eventName || !target) return;
                let key = '';

                if (target === window) {
                    key = `${eventName}${divider}window`;
                } else if (target === document) {
                    key = `${eventName}${divider}document`;
                } else if (typeof target === 'string') {
                    key = `${eventName}${divider}${target}`;
                } else if (target instanceof Element) {
                    // This transform an HTMLElement into a selector, adding a data-event-target attribute.
                    // This is done to serialize the element into a unique key
                    // and to simplify the logic inside the initEventListenerForFeature function.
                    // eslint-disable-next-line prefer-destructuring
                    const dataset = /** @type {HTMLElement} */ (target).dataset;

                    dataset.eventTarget = dataset.eventTarget || `target-${Math.floor(Math.random() * 100000)}`;
                    key = `${eventName}${divider}[data-event-target="${dataset.eventTarget}"]`;
                } else {
                    logger.warn(`Unexpected event configuration :: ${eventName}`, featureConf);

                    return;
                }

                acc[key] = acc[key] || [];
                acc[key].push(featureConf);
            });

            return acc;
        }, {});

        return [featureByEvents, divider];
    }

    /**
     * @param {Record<string, Array<FeatureConfig>>} featureByEvents
     * @param {string} divider
     */
    initFeatureEventListeners(featureByEvents, divider) {
        const onWindowLoad = `load${divider}window`;
        const onContentLoad = `DOMContentLoaded${divider}document`;

        Object.entries(featureByEvents)
            .filter(([key]) => key !== onWindowLoad && key !== onContentLoad)
            .forEach(([key, featuresConf]) => {
                const [eventName, targetSel] = key.split(divider);

                this.initFeatureEventListener(eventName, targetSel, featuresConf);
            });
    }

    /**
     * @param {string} eventName
     * @param {string} targetSel
     * @param {Array<FeatureConfig>} featureConfs
     * @returns {void}
     */
    initFeatureEventListener(eventName, targetSel, featureConfs) {
        if (!eventName || !targetSel) return;

        const targets = targetSel === 'window' ? [window] : targetSel === 'document' ? [document] : Array.from(document.querySelectorAll(targetSel));

        const onEvent = () => {
            this.enqueueFeatures(featureConfs);
            // Remove all listeners when the first event is triggered
            targets.forEach((target) => {
                target.removeEventListener(eventName, onEvent, true);
            });
        };

        targets.forEach((target) => {
            target.addEventListener(eventName, onEvent, true);
        });
    }

    /**
     * Puts feature in the loading queue
     * @param {FeatureConfig} featureConf
     * @returns {void}
     */
    enqueueFeature(featureConf) {
        this.queue.add(featureConf.src, () => this.loadFeature(featureConf), featureConf.priority);
    }

    /**
     * Puts features in the loading queue at the same time
     * @param {Array<FeatureConfig>} featuresConf
     * @returns {void}
     */
    enqueueFeatures(featuresConf) {
        this.queue.stop(); // All the features should be enqueued before re-starting the queue
        featuresConf.forEach((featureConf) => this.enqueueFeature(featureConf));
        this.queue.start();
    }

    /**
     * Load specified feature
     * @param {FeatureConfig} featureConf
     * @returns {Promise<void>}
     */
    loadFeature(featureConf) {
        const { src, type } = featureConf;

        if (!src || !type) {
            logger.warn(`Invalid feature ::`, featureConf);

            return Promise.resolve();
        }

        if (typeof featureConf.enable !== 'undefined' && !featureConf.enable) {
            logger.warn(`Web feature ::`, featureConf);

            return Promise.resolve();
        }

        if (this.loadedScripts[src]) {
            logger.warn(`${type.toUpperCase()} ${src} :: load warn :: already loaded`);

            return Promise.resolve();
        }

        this.loadedScripts[src] = 'pending';

        const firstLetter = type.charAt(0).toUpperCase();
        const restWord = type.substring(1, type.length);
        const loaderName = `load${firstLetter}${restWord}`;

        return this[loaderName](featureConf)
            .then(() => {
                this.loadedScripts[src] = 'loaded';
                logger.log(`${type.toUpperCase()} ${src} :: load success!`);
            })
            .catch((err) => {
                this.loadedScripts[src] = 'error';
                logger.error(`${type.toUpperCase()} ${src} :: load error ::`, err);
            });
    }

    /**
     * Load style module via async call
     * @param {FeatureConfig} featureConf
     * @returns {Promise<FeatureConfig>}
     */
    loadStyle(featureConf) {
        if (featureConf.src.match(/^(https?)|\/\/.*/)) {
            loadCSS(featureConf.src);

            return Promise.resolve(featureConf);
        }

        return new Promise((resolve, reject) => {
            import(`./scss/${featureConf.src}.scss`)
                .then(() => resolve(featureConf))
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Load internal module via async call
     * @param {FeatureConfig} featureConf
     * @returns {Promise<FeatureConfig | false>}
     */
    loadModule(featureConf) {
        const folders = featureConf.src.split('/');
    /* eslint-disable */
    switch (folders.length) {
      case 1:
        return new Promise((resolve, reject) => {
          import(
            /* webpackChunkName: "[request]" */ `../src/features/${folders[0]}/feature`
          )
            .then(({ default: Feature }) => {
              this.resolveModuleLoad(Feature, featureConf, resolve);
            })
            .catch(error => {
              reject(error);
            });
        });
      case 2:
        return new Promise((resolve, reject) => {
          import(
            /* webpackChunkName: "[request]" */ `../src/features/${folders[0]}/${folders[1]}/feature`
          )
            .then(({ default: Feature }) => {
              this.resolveModuleLoad(Feature, featureConf, resolve);
            })
            .catch(error => {
              reject(error);
            });
        });
      case 3:
        return new Promise((resolve, reject) => {
          import(
            /* webpackChunkName: "[request]" */ `../src/features/${folders[0]}/${folders[1]}/${folders[2]}/feature`
          )
            .then(({ default: Feature }) => {
              this.resolveModuleLoad(Feature, featureConf, resolve);
            })
            .catch(error => {
              reject(error);
            });
        });
      case 4:
        return new Promise((resolve, reject) => {
          import(
            /* webpackChunkName: "[request]" */ `../src/features/${folders[0]}/${folders[1]}/${folders[2]}/${folders[3]}/feature`
          )
            .then(({ default: Feature }) => {
              this.resolveModuleLoad(Feature, featureConf, resolve);
            })
            .catch(error => {
              reject(error);
            });
        });
      default:
        return Promise.resolve(false);
            /* eslint-enable */
        }
    }

    /**
     * Resolve loading of module
     * @param {new () => { load: Function }} Feature loaded from  module
     * @param {FeatureConfig} featureConf object with details configured for this module
     * @param {(value: FeatureConfig) => void} resolve function for Promise
     * @returns {void}
     */
    resolveModuleLoad(Feature, featureConf, resolve) {
        const feature = new Feature();

        if (feature.load) {
            try {
                feature.load();
            } catch (e) {
                logger.error('Feature error:', {
                    name: featureConf.src,
                    error: e.message,
                });
            }

            // A feature error is not an orchestrator error, therefore this should always resolve
            resolve(featureConf);
        }
    }

    /**
     * Load external script via async call
     * @param {FeatureConfig} featureConf
     * @returns {Promise<FeatureConfig>}
     */
    loadExt(featureConf) {
        return new Promise((resolve) => {
            if (featureConf.legacy && !this.isSupportModules) {
                featureConf.src = this.getLegacyUrl(featureConf.src);
            }
            this.loadScript(featureConf, resolve);
        });
    }

    /**
     * @param {FeatureConfig} featureConf
     * @param {(value: FeatureConfig) => void} resolve
     * @param {string} [type]
     * @returns {void}
     */
    loadScript(featureConf, resolve, type) {
        const onLoad = (err) => {
            if (featureConf.callback) {
                try {
                    featureConf.callback(err);
                } catch (e) {
                    logger.error('Feature error:', {
                        name: featureConf.src,
                        error: e.message,
                    });
                }
            }

            // A feature error is not an orchestrator error, therefore this should always resolve
            resolve(featureConf);
        };

        load(featureConf.src, type, {
            callback: onLoad,
            context: this,
        });
    }

    /**
     * @param {string} url
     * @returns {string}
     */
    getLegacyUrl(url) {
        let legacyUrl = '';

        if (url.includes('.min.js')) {
            legacyUrl = url.replace('.min.js', '.legacy.min.js');
        } else {
            legacyUrl = url.replace('.js', '.legacy.js');
        }

        return legacyUrl;
    }
}

/**
 * @typedef {Object} FeatureConfig
 * @property {'ext' | 'module' | 'style'} type
 * @property {string} src
 * @property {number} priority
 *
 * @property {Array<'desktop' | 'mobile' | 'tablet'>} [flavors]
 * @property {boolean} [enableOffline]
 * @property {boolean} [onlyOffline]
 * @property {boolean} [legacy]
 *
 * @property {Array<{ eventName: string, target: EventTarget }>} [events]
 * @property {{ target: string | Array<string>, offset?: number }} [onViewport]
 * @property {{ targets: Array<string> }} [onElementsPresence]
 *
 * @property {(err: Error) => void} [callback] -- only if type === 'ext'
 */

/**
 * @typedef {string | Window | Document | Element } EventTarget
 */

/**
 * @typedef {Object} OrchestratorConfig
 * @property {Array<FeatureConfig>} components
 * @property {Array<*>} listeners
 * @property {*} ptm
 */

/**
 * @typedef {Object} RcsTagManagerConfig
 * @property {string} profileId
 */
